import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  HelpCircle, 
  Edit, 
  Plus,
  BookOpen,
  Settings,
  Users as UsersIcon,
  BarChart3,
  Target
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface HelpContent {
  id: string;
  key: string;
  content: string;
  category: string;
  updated_at: string;
}

const CATEGORY_INFO = {
  'Maturity Assessment': {
    icon: BarChart3,
    description: 'Help content for maturity assessment module',
    color: 'bg-blue-100 text-blue-800'
  },
  'Value Engineering': {
    icon: Target,
    description: 'Help content for value engineering workflows',
    color: 'bg-green-100 text-green-800'
  },
  'ROI Analysis': {
    icon: BookOpen,
    description: 'Help content for ROI calculation and analysis',
    color: 'bg-purple text-purple'
  },
  'Admin Functions': {
    icon: Settings,
    description: 'Help content for administrative functions',
    color: 'bg-orange-100 text-orange-800'
  },
  'User Management': {
    icon: UsersIcon,
    description: 'Help content for user and project management',
    color: 'bg-pink-100 text-pink-800'
  },
  'General': {
    icon: HelpCircle,
    description: 'General help content and guidance',
    color: 'bg-gray-100 text-gray-800'
  }
};

export default function AdminHelpScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [editingContent, setEditingContent] = useState<HelpContent | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newContent, setNewContent] = useState({
    key: '',
    content: '',
    category: 'General'
  });

  // Fetch help content with caching
  const { data: helpContent = [], isLoading } = useQuery<HelpContent[]>({
    queryKey: ['/api/help-content'],
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  // Group content by category
  const contentByCategory = (Array.isArray(helpContent) ? helpContent : []).reduce((acc, content) => {
    const category = content.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(content);
    return acc;
  }, {} as Record<string, HelpContent[]>);

  // Filter content based on selected category
  const filteredContent = selectedCategory === 'all' 
    ? (Array.isArray(helpContent) ? helpContent : []) 
    : (Array.isArray(helpContent) ? helpContent : []).filter(content => content.category === selectedCategory);

  const updateContentMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<HelpContent> }) => {
      return apiRequest(`/api/help-content/${id}`, {
        method: 'PATCH',
        body: updates,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/help-content'] });
      toast({ title: "Success", description: "Help content updated successfully" });
      setEditingContent(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update help content", variant: "destructive" });
    }
  });

  const createContentMutation = useMutation({
    mutationFn: async (contentData: typeof newContent) => {
      return apiRequest('/api/help-content', {
        method: 'POST',
        body: contentData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/help-content'] });
      toast({ title: "Success", description: "Help content created successfully" });
      setIsCreateDialogOpen(false);
      setNewContent({ key: '', content: '', category: 'General' });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create help content", variant: "destructive" });
    }
  });

  const extractTitleFromKey = (key: string) => {
    // Convert dot notation to readable title
    const parts = key.split('.');
    const lastPart = parts[parts.length - 1];
    
    // Convert camelCase to Title Case
    return lastPart
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const formatContentPreview = (content: string) => {
    return content.length > 100 ? content.substring(0, 100) + '...' : content;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading help content...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Help Content Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage help content and guidance across all platform modules
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Help Content
        </Button>
      </div>

      {/* Category Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Object.entries(CATEGORY_INFO).map(([category, info]) => {
          const Icon = info.icon;
          const count = contentByCategory[category]?.length || 0;
          
          return (
            <Card key={category} className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedCategory(category)}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${info.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{category}</div>
                    <div className="text-xs text-gray-500">{count} items</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <Label htmlFor="category-filter">Filter by Category:</Label>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.keys(CATEGORY_INFO).map(category => (
              <SelectItem key={category} value={category}>{category}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="ml-2">
          {filteredContent.length} items
        </Badge>
      </div>

      {/* Help Content Table */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <HelpCircle className="w-5 h-5" />
            <span>Help Content Library</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Content Preview</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContent.map((content) => {
                const categoryInfo = CATEGORY_INFO[content.category as keyof typeof CATEGORY_INFO] || CATEGORY_INFO['General'];
                const Icon = categoryInfo.icon;
                
                return (
                  <TableRow key={content.id}>
                    <TableCell>
                      <div className="font-medium">
                        {extractTitleFromKey(content.key)}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {content.key}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Icon className="w-4 h-4 text-gray-500" />
                        <Badge className={categoryInfo.color}>
                          {content.category}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        {formatContentPreview(content.content)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500">
                        {new Date(content.updated_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingContent(content)}
                        className="flex items-center space-x-1"
                      >
                        <Edit className="w-3 h-3" />
                        <span>Edit</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Content Dialog */}
      <Dialog open={!!editingContent} onOpenChange={() => setEditingContent(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Help Content</DialogTitle>
          </DialogHeader>
          {editingContent && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit_title">Title</Label>
                <Input
                  id="edit_title"
                  value={extractTitleFromKey(editingContent.key)}
                  disabled
                  className="bg-gray-100 dark:bg-gray-800"
                />
                <div className="text-xs text-gray-500 mt-1">Key: {editingContent.key}</div>
              </div>
              <div>
                <Label htmlFor="edit_category">Category</Label>
                <Select 
                  value={editingContent.category} 
                  onValueChange={(value) => setEditingContent({ ...editingContent, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(CATEGORY_INFO).map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_content">Content</Label>
                <Textarea
                  id="edit_content"
                  value={editingContent.content}
                  onChange={(e) => setEditingContent({ ...editingContent, content: e.target.value })}
                  rows={8}
                  className="resize-none"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingContent(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => editingContent && updateContentMutation.mutate({ 
                id: editingContent.id, 
                updates: { content: editingContent.content, category: editingContent.category } 
              })}
              disabled={updateContentMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {updateContentMutation.isPending ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Content Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Help Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new_key">Content Key</Label>
              <Input
                id="new_key"
                value={newContent.key}
                onChange={(e) => setNewContent({ ...newContent, key: e.target.value })}
                placeholder="e.g., maturity.assessment.overview"
              />
              <div className="text-xs text-gray-500 mt-1">
                Use dot notation for hierarchical organization
              </div>
            </div>
            <div>
              <Label htmlFor="new_category">Category</Label>
              <Select 
                value={newContent.category} 
                onValueChange={(value) => setNewContent({ ...newContent, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(CATEGORY_INFO).map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="new_content">Content</Label>
              <Textarea
                id="new_content"
                value={newContent.content}
                onChange={(e) => setNewContent({ ...newContent, content: e.target.value })}
                rows={8}
                className="resize-none"
                placeholder="Enter the help content..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => createContentMutation.mutate(newContent)}
              disabled={createContentMutation.isPending || !newContent.key || !newContent.content}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {createContentMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
